import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { applyOverrides } from "@/lib/uiOverrides";

const API = process.env.REACT_APP_BACKEND_URL;
const Ctx = createContext({ overrides: {}, refresh: () => {} });

/* Loads admin-defined icon/label overrides once and re-checks when the tab regains focus. */
export function UiOverridesProvider({ children }) {
    const [overrides, setOverrides] = useState({});

    const refresh = useCallback(() => {
        axios.get(`${API}/api/public/ui-overrides`).then(({ data }) => setOverrides(data?.overrides || {})).catch(() => {});
    }, []);

    useEffect(() => {
        refresh();
        let last = Date.now();
        const onFocus = () => { if (Date.now() - last > 15000) { last = Date.now(); refresh(); } };
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, [refresh]);

    const value = useMemo(() => ({ overrides, refresh }), [overrides, refresh]);
    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useUiOverrides = () => useContext(Ctx).overrides;

/* Returns a copy of a nav structure with admin label/icon overrides applied. */
export function useUiStructure(structure, prefix, options) {
    const overrides = useUiOverrides();
    return useMemo(() => applyOverrides(structure, prefix, overrides, options), [structure, prefix, overrides, options]);
}

/* label(key, fallback) for one-off headings. */
export function useUiLabel() {
    const overrides = useUiOverrides();
    return useCallback((key, fallback) => overrides[key]?.label || fallback, [overrides]);
}
